'use client'

import {useEffect, useId, useRef, useState} from 'react'
import {flushSync} from 'react-dom'
import {descriptionPreview, resolveDescriptionContent, richContentText} from '@fitro/rich-content'
import {Button} from './Button'
import {RichContent} from './RichContent'
import {SectionAccordion} from './SectionAccordion'

export interface DescriptionContentProps {
    description?: string | null
    fullDescriptionHtml?: string | null
    assetBaseUrl?: string | null
    labels: {expand: string; collapse: string; full: string}
    /** A heading above the description, where it sits among other details. */
    title?: string
}

/** Caller keys this component by entity, never by refetched content. */
export function DescriptionContent(props: DescriptionContentProps) {
    if (!resolveDescriptionContent(props.description, props.fullDescriptionHtml).intro) return null
    return <div className="space-y-4">
        <DescriptionIntro {...props}/>
        <FullDescriptionSection {...props}/>
    </div>
}

/** Intro below the summary, aligned with section-header text. */
export function DescriptionIntro({description, fullDescriptionHtml, assetBaseUrl, labels, title}: DescriptionContentProps) {
    const {intro} = resolveDescriptionContent(description, fullDescriptionHtml)
    return intro ? <div className="px-4" data-description-intro>
        <DescriptionTitle title={title}/>
        <ExpandableDescription content={intro} assetBaseUrl={assetBaseUrl} labels={labels}/>
    </div> : null
}

export function FullDescriptionSection({description, fullDescriptionHtml, assetBaseUrl, labels}: DescriptionContentProps) {
    const {additionalHtml} = resolveDescriptionContent(description, fullDescriptionHtml)
    const [open, setOpen] = useState(false)
    if (!additionalHtml) return null
    return <SectionAccordion title={labels.full} isOpen={open} onToggle={() => setOpen(v => !v)}>
        {open ? <RichContent html={additionalHtml} assetBaseUrl={assetBaseUrl}/> : null}
    </SectionAccordion>
}

const INLINE_COLLAPSED_REM = {text: 3, html: 8} as const
// Matches `duration-normal`, the height transition below.
const INLINE_TRANSITION_MS = 200

/** A details-window description: two lines (or an image preview), then the whole description in place. */
export function InlineDescription({description, fullDescriptionHtml, assetBaseUrl, labels, title}: DescriptionContentProps) {
    const {intro, additionalHtml} = resolveDescriptionContent(description, fullDescriptionHtml)
    const collapsedRem = INLINE_COLLAPSED_REM[intro?.kind ?? 'text']
    const collapsedHeight = `${collapsedRem}rem`
    const [expanded, setExpanded] = useState(false)
    // The rest of the description mounts to open and stays until the collapse finishes.
    const [additionalShown, setAdditionalShown] = useState(false)
    const [overflow, setOverflow] = useState(false)
    // A pixel height while the height moves; `none` once open, so the text can reflow.
    const [maxHeight, setMaxHeight] = useState<string>(collapsedHeight)
    const clipped = useRef<HTMLDivElement>(null)
    const introRef = useRef<HTMLDivElement>(null)
    const settle = useRef<ReturnType<typeof setTimeout> | null>(null)
    const id = useId()
    useEffect(() => {
        const element = clipped.current
        if (!element || expanded) return
        const measure = () => setOverflow(element.scrollHeight > element.clientHeight + 1)
        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(element)
        element.addEventListener('load', measure, true)
        return () => {observer.disconnect(); element.removeEventListener('load', measure, true)}
    }, [expanded, intro?.value])
    useEffect(() => () => {if (settle.current) clearTimeout(settle.current)}, [])
    if (!intro) return null
    const toggle = () => {
        const element = clipped.current
        if (!element) return
        if (settle.current) clearTimeout(settle.current)
        // Pin the visible height, so the transition starts where the text is, and commit it before moving.
        flushSync(() => setMaxHeight(`${element.offsetHeight}px`))
        void element.offsetHeight
        if (expanded) {
            const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
            const target = Math.min(introRef.current?.offsetHeight ?? 0, collapsedRem * remPx)
            setExpanded(false)
            setMaxHeight(`${target}px`)
            settle.current = setTimeout(() => {
                setAdditionalShown(false)
                setMaxHeight(collapsedHeight)
            }, INLINE_TRANSITION_MS)
        } else {
            flushSync(() => {
                setExpanded(true)
                setAdditionalShown(true)
            })
            setMaxHeight(`${element.scrollHeight}px`)
            settle.current = setTimeout(() => setMaxHeight('none'), INLINE_TRANSITION_MS)
        }
    }
    return <div className="min-w-0 break-words text-base leading-6 text-gray-800 dark:text-gray-200">
        <DescriptionTitle title={title}/>
        <div ref={clipped} id={id} style={{maxHeight}}
            // An image that loads while opening grows the content; follow it instead of jumping once settled.
            onLoadCapture={() => {if (expanded && maxHeight !== 'none' && clipped.current) setMaxHeight(`${clipped.current.scrollHeight}px`)}}
            className={maxHeight === 'none' ? undefined : 'overflow-hidden transition-[max-height] duration-normal ease-out motion-reduce:transition-none'}>
            <div ref={introRef}>
                {intro.kind === 'text' ? <p className="whitespace-pre-wrap">{intro.value}</p> : <RichContent html={intro.value} assetBaseUrl={assetBaseUrl}/>}
            </div>
            {additionalHtml && additionalShown ? <div className="mt-2" aria-hidden={!expanded || undefined} inert={!expanded || undefined}>
                <RichContent html={additionalHtml} assetBaseUrl={assetBaseUrl}/>
            </div> : null}
        </div>
        {overflow || additionalHtml ? <Button type="button" variant="ghost" size="inline" className="font-semibold leading-6"
            aria-expanded={expanded} aria-controls={id} onClick={toggle}>
            {expanded ? labels.collapse : labels.expand}
        </Button> : null}
    </div>
}

function DescriptionTitle({title}: {title?: string}) {
    return title ? <h3 className="mb-1 text-sm font-semibold leading-5 text-gray-900 dark:text-gray-100">{title}</h3> : null
}

const COLLAPSED_DESCRIPTION_REM = 12
const CLAMP_BLOCKS = 'p, li, h1, h2, h3, h4, h5, h6, blockquote, pre, img, figure, table'

/** The tallest height within `limit` that ends after a whole line, so no line is cut in half. */
function wholeLinesHeight(root: HTMLElement, limit: number): number {
    const top = root.getBoundingClientRect().top
    let cut = 0
    // A heading shown without any of its text below is cut together with it.
    let beforeHeading = 0
    for (const block of root.querySelectorAll<HTMLElement>(CLAMP_BLOCKS)) {
        const box = block.getBoundingClientRect()
        const start = box.top - top
        if (start >= limit) break
        const end = box.bottom - top
        const heading = /^H[1-6]$/.test(block.tagName)
        if (end <= limit) {
            beforeHeading = heading ? cut : 0
            cut = Math.max(cut, end)
            continue
        }
        // A block crossing the limit keeps the lines that fit; media are never cut.
        if (!['IMG', 'FIGURE', 'TABLE'].includes(block.tagName)) {
            const lineHeight = parseFloat(getComputedStyle(block).lineHeight)
            const lines = lineHeight > 0 ? Math.floor((limit - start) / lineHeight) : 0
            if (lines > 0) {
                cut = Math.max(cut, start + lines * lineHeight)
                beforeHeading = 0
            }
        }
    }
    if (beforeHeading > 0) cut = beforeHeading
    return cut > 0 ? Math.ceil(cut) : limit
}

function ExpandableDescription({content, assetBaseUrl, labels}: {
    content: {kind: 'text' | 'html'; value: string}
    assetBaseUrl?: string | null
    labels: DescriptionContentProps['labels']
}) {
    const [expanded, setExpanded] = useState(false)
    const [overflow, setOverflow] = useState(false)
    const [clipHeight, setClipHeight] = useState<number | null>(null)
    const measured = useRef<HTMLDivElement>(null)
    const id = useId()
    const preview = descriptionPreview(content.kind === 'text' ? content.value : richContentText(content.value))
    useEffect(() => {
        if (content.kind !== 'html' || !measured.current) return
        const element = measured.current
        const measure = () => {
            const limit = COLLAPSED_DESCRIPTION_REM * parseFloat(getComputedStyle(document.documentElement).fontSize)
            const overflowing = element.scrollHeight > limit + 1
            setOverflow(overflowing)
            setClipHeight(overflowing ? wholeLinesHeight(element, limit) : null)
        }
        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(element)
        element.addEventListener('load', measure, true)
        return () => {observer.disconnect(); element.removeEventListener('load', measure, true)}
    }, [content.kind, content.value])
    const expandable = content.kind === 'text' ? preview.truncated : overflow
    const clipped = content.kind === 'html' && overflow && !expanded
    const toggle = expandable ? <Button type="button" variant="ghost" size="inline" className="font-semibold leading-6"
        aria-expanded={expanded} aria-controls={id} onClick={() => setExpanded(v => !v)}>
        {expanded ? labels.collapse : labels.expand}
    </Button> : null
    return <div className="min-w-0 break-words text-base leading-6 text-gray-800 dark:text-gray-200">
        {content.kind === 'text' ? <p className="whitespace-pre-wrap">
            <span id={id}>{expanded ? content.value : preview.text}</span>{toggle ? <> {toggle}</> : null}
        </p> : <>
            <div id={id}>
                {clipped ? <p className="sr-only">{preview.text}</p> : null}
                <div className={clipped ? 'overflow-hidden' : undefined} style={clipped && clipHeight ? {maxHeight: clipHeight} : undefined} aria-hidden={clipped || undefined} inert={clipped || undefined}>
                    <div ref={measured}><RichContent html={content.value} assetBaseUrl={assetBaseUrl}/></div>
                </div>
            </div>
            {toggle}
        </>}
    </div>
}
